import { Injectable }                        from "@angular/core";
import { RequestOptions, URLSearchParams  } from "@angular/http";
import { CONFIGURATION }                     from "../../../../app.constants";
import { Diocese, Organisation, JobTitle, AccountRole }
from "../../../../models";
import { Observable }                        from "rxjs/Observable";
import { HttpDataService }                   from "../../../../services/shared";

@Injectable()
export class UserOrganisationInfoService {

    constructor(private httpData: HttpDataService) { }
    
    public GetDioceseList(): Observable<Diocese[]> {
        return Observable.create((observer) => {
            this.httpData.get(CONFIGURATION.baseUrls.apiUrl + "v1/EF/SearchDiocese?$orderby=DioceseName asc")
                .map(response => <Diocese[]>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetOrganisationsByDiocese(diocesePublicId: string): Observable<Organisation[]> {
        return Observable.create((observer) => {
            this.httpData.get(CONFIGURATION.baseUrls.apiUrl + "v1/EF/GetDiocese/" + diocesePublicId)
                .map(response => <Organisation[]>response.json().result.organisations)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetOrganisationsByDioceseId(dioceseId: number): Observable<Organisation[]> {
        const enterpriseConnectionName = ""; // Return All organisations until Auth0 connectionName issue is rectified
        let enterpriseConnectionNameParam: string = enterpriseConnectionName ? `&enterpriseConnectionName=${encodeURIComponent(enterpriseConnectionName)}` : "";
        let select: string = "$select=PublicId,Name,LocationSuburb,LocationPostCode,ExternalId";
        let filter: string = `&$filter=DioceseId eq ${dioceseId} and IsActive eq true`;
        let order: string = "&$orderby=Name";
        let dataUrl = CONFIGURATION.baseUrls.apiUrl + "v1/EF/SearchOrganisation?" + select + filter + order + enterpriseConnectionNameParam;

        return Observable.create((observer) => {
            this.httpData.get(dataUrl)
                .map(response => <Organisation[]>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetOrganisationsByConnectionName(enterpriseConnectionNames: string[] = []): Observable<Organisation[]> {
        const select: string = "$select=PublicId,Name,LocationSuburb,LocationPostCode,ExternalId,EnterpriseConnectionName";
        const order: string = "&$orderby=Name";

        let filterExpression = "IsActive eq true";

        // If no connection names provided, inject an empty string so the filter explicitly matches blanks
        if (!enterpriseConnectionNames || enterpriseConnectionNames.length === 0) {
            enterpriseConnectionNames = [''];
        }
        if (enterpriseConnectionNames && enterpriseConnectionNames.length > 0) {
            const clauses = enterpriseConnectionNames
                .filter(n => n !== undefined && n !== null) // keep empty string values
                .map(n => n.trim()) // trim but preserve empty strings
                .map(n => n.replace(/'/g, "''")) // escape single quotes for OData
                .map(n => `EnterpriseConnectionName eq '${n}'`);
            if (clauses.length > 0) {
                filterExpression += " and (" + clauses.join(" or ") + ")";
            }
        }

        // Encode the whole filter expression for URL safety
        const filter: string = `&$filter=${encodeURIComponent(filterExpression)}`;
        const dataUrl = CONFIGURATION.baseUrls.apiUrl + "v1/EF/SearchOrganisation?" + select + filter + order;
        return Observable.create((observer) => {
            this.httpData.get(dataUrl)
                .map(response => <Organisation[]>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetOrganisationByExtOrganisationId(extOrganisationId: string, userId: string): Observable<Organisation> {
        let dataUrl = CONFIGURATION.baseUrls.apiUrl + "organisation?extOrganisationId=" + extOrganisationId + "&userId=" + userId;

        return Observable.create((observer) => {
            this.httpData.get(dataUrl)
                .map(response => <Organisation>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetJobTitles(): Observable<JobTitle[]> {
        let dataUrl = `${CONFIGURATION.baseUrls.apiUrl}common/getpositions`;

        return Observable.create((observer) => {
            this.httpData.get(dataUrl)
                .map(response => <JobTitle[]>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public GetRoles(): Observable<AccountRole[]> {
        return Observable.create((observer) => {
            this.httpData.get(CONFIGURATION.baseUrls.apiUrl + "common/GetRoles")
                .map(response => <AccountRole[]>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public Add(organisation: Organisation): Observable<Organisation> {
        return Observable.create((observer) => {
            this.httpData.post(CONFIGURATION.baseUrls.apiUrl + "organisation", JSON.stringify(organisation))
                .map(response => <Organisation>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    public Update(organisation: Organisation): Observable<Organisation> {
        return Observable.create((observer) => {
            this.httpData.put(CONFIGURATION.baseUrls.apiUrl + "organisation", JSON.stringify(organisation))
                .map(response => <Organisation>response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }

    // Given the user's position and global role code, the method should return the actual role the user has in the organisation
    // Calls server's GET api/organisation
    // Since positions such as Teacher, Additional Needs Officer, Board Members, Director, etc can still be Administrator but by default should be Standard,
    // we use enforceChange boolean value to indicate if the API should change the resulting roleCode for non-administrative positions.
    // This API is called both when the edit page is loaded (which should not affect the original roleCode of the non-admin people), and 
    // when user changes the position of a person (which should affect the roleCode of the non-admin positions).
    public GetRoleByPositionCode(positionCode: string, roleCode: string, enforceChange: boolean = false): Observable<Organisation> {
        let params = new URLSearchParams();
        if (positionCode) params.set("positionCode", positionCode);
        if (roleCode) params.set("roleCode", roleCode);
        params.set("enforceChange", String(enforceChange));
        let args = new RequestOptions({
            search: params
        });
        return Observable.create((observer) => {
            this.httpData.get(CONFIGURATION.baseUrls.apiUrl + "organisation/GetOrganisationRole", args)
                .map(response => response.json().result)
                .subscribe((userData) => {
                    observer.next(userData);
                }, (error) => observer.error(error),
                () => observer.complete());
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/userHome/userDetails/organisationInfo/organisationInfo.service.ts